import { Component, Input, OnInit } from '@angular/core';
import { TranslateService }         from '@ngx-translate/core';
import { UIRouter }                 from '@uirouter/core';

import { CompanyService }       from '../companies/company.service';
import { CustomerModel }        from '../customers/models/customer';
import { EditComponent }        from '../table/cell-components/actions/edit.component';
import { InvoiceModel }         from '../invoices/models/invoice';
import { MessagesService }      from '../messages/messages.service';
import { Table }                from '../table/types/table';
import { TableColumn }          from '../table/types/table-column';
import { TableQueryParameters } from '../table/types/table-query-parameters';
import { TableRowEvent }        from '../table/types/table-row-event';
import { TableService }         from '../table/table.service';
import { VehicleModel }         from './models/vehicle';
import { VehiclesService }      from './vehicles.service';

@Component({
  selector   : 'wor-vehicle-invoices',
  template: require('./vehicle.invoices.component.html')
})
export class VehicleInvoicesComponent implements OnInit {
  @Input() customer: CustomerModel       = {} as CustomerModel;
  @Input() invoices: Array<InvoiceModel> = [];
  @Input() vehicle : VehicleModel        = {} as VehicleModel;

  collapsed = false;

  tableSettings: Table;

  constructor (
    private companyService  : CompanyService,
    private messagesService : MessagesService,
    private router          : UIRouter,
    private tableService    : TableService,
    private translateService: TranslateService,
    private vehiclesService : VehiclesService
  ) {}

  ngOnInit () : void {
    this.collapsed = !this.invoices.length;

    const columns : Array<TableColumn> = [
      {
        field : 'invoice_number',
        header: {
          text: this.translateService.instant('INVOICE_SPACE.INVOICE_NUMBER')
        }
      },
      {
        field : 'post_date',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.POST_DATE')
        },
        pipe: 'globalDate'
      },
      {
        field : 'description',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.DESCRIPTION')
        }
      },
      {
        field : 'invoice_type',
        header: {
          text: this.translateService.instant('JS_SPACE.COLUMNS.TRAN_TYPE')
        },
        pipe: 'invoiceType'
      },
      {
        field : 'invoice_status',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.STATUS')
        },
        pipe: 'invoiceStatus'
      },
      {
        field : 'total',
        header: {
          text: this.translateService.instant('JS_SPACE.COLUMNS.AMOUNT')
        },
        pipe: 'globalCurrency'
      },
      {
        field : 'balance_due',
        header: {
          text: this.translateService.instant('JS_SPACE.COLUMNS.BALANCE')
        },
        pipe: 'globalCurrency'
      },
      {
        component: {
          bindings: {
            outputs: {
              onEditClick: ( invoice : InvoiceModel ) => this.view(invoice)
            }
          },
          enabled: !this.vehicle.archived,
          factory: EditComponent,
        },
        sortable: false,
      }
    ];

    if ( this.companyService.isMultiBranch() ) {
      columns.unshift({
        field : 'store_name',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.STORE_NAME')
        }
      });
    }

    this.tableSettings = {
      cache: {
        enabled: true,
        key    : this.vehiclesService.invoicesTableCacheKey
      },
      columns: columns,
      empty  : {
        text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_INVOICES_FOUND')
      },
      pagination: {
        enabled   : true,
        pageSize  : this.tableService.getCachedPaging(this.vehiclesService.invoicesTableCacheKey).pageSize || 5,
        pageSizes : [ 5, 10, 20, 50 ],
        queryCount: () => this.vehiclesService.getInvoicesCount(this.vehicle)
      },
      rows: {
        highlight: {
          enabled: true
        },
      },
      sorting: this.tableService.setSortingFromCache(this.vehiclesService.invoicesTableCacheKey, {
        direction: 'desc',
        field    : 'post_date'
      })
    };
  }

  get ( params : TableQueryParameters ) : Promise<Array<InvoiceModel>> {
    return this.vehiclesService.getInvoices(this.vehicle, params)
      .then(( invoices : Array<InvoiceModel> ) => {
        this.invoices = invoices;

        return invoices;
      })
      .catch(error => {
        this.messagesService.error(error);

        return this.invoices;
      });
  }

  onRowDoubleClick ( event : TableRowEvent ) : void {
    if ( !this.vehicle.archived ) {
      this.view(event.row);
    }
  }

  view ( invoice : InvoiceModel ) : void {
    if ( invoice.outside_company ) {
      this.vehiclesService.openInvoice(invoice);
    }
    else {
      this.router.stateService.go('app.invoice', {
        customer_id: this.customer.id,
        id         : invoice.id,
        vehicle_id : invoice.vehicle_id
      });
    }
  }
}
